import React, { useState } from 'react';
import { Container, Box, Link, useTheme, useMediaQuery } from '@mui/material';
import { Button, Stepper, Typography, Dialog } from '@sm-dashboard/ui-components';
import instanceAxios from '../../../util/axios';

import BusinessInfoForm from '../../../components/dashboard/BusinessAds/public/business-ad-submission/BusinessInfoForm';
import CategorySelector from '../../../components/dashboard/BusinessAds/public/business-ad-submission/CategorySelector';
import DescriptionAndMediaForm from '../../../components/dashboard/BusinessAds/public/business-ad-submission/DescriptionAndMediaForm';
import AdditionalInfoForm from '../../../components/dashboard/BusinessAds/public/business-ad-submission/AdditionalInfoForm';
import PaymentPlanSelector from '../../../components/dashboard/BusinessAds/public/business-ad-submission/PaymentPlanSelector';
import WhatsAppSupportButton from '../../../components/dashboard/BusinessAds/WhatsAppSupportButton';
import {
  PUBLISHER_EXPLANATION_TEXT,
  PUBLISHER_INFO_LINK,
  PUBLISHER_REGULATIONS
} from '../../../constants/businessAds.constants';
import SubmissionSuccess from '../../../components/common/SubmissionSuccess';
import { MOBILE_BOTTOM_NAV_INSET } from '@/layouts/mobileLayoutConstants';
import { publicBusinessAdsUrl } from '@/constants';

const initialFormData = {
  submitterType: 'publisher' as 'publisher' | 'shaliach',
  businessName: '',
  businessNameForInvoicing: '',
  businessEmail: '',
  businessPhone: '',
  hasWhatsapp: true,
  category: '',
  subCategory: [] as string[],
  shortDescription: '',
  fullDescription: '',
  location: [] as string[],
  minPrice: '' as number | '',
  maxPrice: '' as number | '',
  targetAudience: [] as string[],
  shabadHouseDiscount: '',
  mainImage: '',
  additionalMedia: [] as string[],
  websiteLink: '',
  facebookLink: '',
  instagramLink: '',
  recommendations: '',
  paymentPlan: ''
};

const PublisherAdSubmissionPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [createdAdId, setCreatedAdId] = useState<string | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [regulationsOpen, setRegulationsOpen] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const steps = ['פרטי העסק', 'קטגוריה', 'תיאור ומדיה', 'מידע נוסף', 'תשלום ותקנון', 'תשלום'];

  const handleNext = () => setActiveStep(prev => prev + 1);
  const handleBack = () => setActiveStep(prev => prev - 1);

  const handleReset = () => {
    setActiveStep(0);
    setFormData(initialFormData);
    setSuccess(false);
    setError('');
    setAgreed(false);
  };

  // Validation helper functions
  const isValidEmail = (email: string) => {
    if (!email) return false; // Email is now required
    const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
    return emailRegex.test(email);
  };

  const isValidPhone = (phone: string) => {
    const phoneRegex = /^[0-9]{10}$|^0[0-9]{1,2}-?[0-9]{7}$|^\+972-?[0-9]{1,2}-?[0-9]{7}$/;
    return phoneRegex.test(phone);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      // Remove empty string values for optional numeric fields
      const dataToSend = {
        ...formData,
        minPrice: formData.minPrice === '' ? undefined : formData.minPrice,
        maxPrice: formData.maxPrice === '' ? undefined : formData.maxPrice
      };
      const response = await instanceAxios.post(publicBusinessAdsUrl, dataToSend);
      setCreatedAdId(response.data._id);

      // If pilot plan, skip payment and go directly to success
      if (formData.paymentPlan === 'pilot-half-yearly') {
        setSuccess(true);
      } else {
        setActiveStep(5); // Move to payment step
      }
    } catch (err) {
      setError('התרחשה שגיאה במהלך שליחת הטופס. אנא נסה שוב.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!createdAdId) return;

    setPaymentLoading(true);
    setError('');
    try {
      console.log('Creating payment session for ad:', createdAdId);
      const response = await instanceAxios.post(`${publicBusinessAdsUrl}/${createdAdId}/payment-session`);
      console.log('Payment session response:', response.data);

      if (response.data.paymentUrl) {
        // Redirect to CardCom payment page
        window.location.href = response.data.paymentUrl;
      } else {
        setError('לא התקבל קישור תשלום מהשרת');
      }
    } catch (err) {
      console.error('Payment session error:', err);
      setError('התרחשה שגיאה ביצירת התשלום. אנא נסה שוב.');
    } finally {
      setPaymentLoading(false);
    }
  };

  const getValidationErrors = () => {
    const errors: string[] = [];
    switch (activeStep) {
      case 0:
        if (!formData.businessName.trim()) errors.push('שם העסק');
        if (!formData.businessNameForInvoicing.trim()) errors.push('שם העסק לחשבונית');
        if (!formData.businessEmail.trim()) errors.push('אימייל העסק');
        else if (!isValidEmail(formData.businessEmail)) errors.push('אימייל תקין');
        if (!formData.businessPhone.trim()) errors.push('טלפון העסק');
        else if (!isValidPhone(formData.businessPhone)) errors.push('טלפון תקין');
        break;
      case 1:
        if (!formData.category.trim()) errors.push('קטגוריה');
        break;
      case 2:
        if (!formData.shortDescription.trim()) errors.push('תיאור קצר');
        break;
      case 3:
        if (formData.location.length === 0) errors.push('מיקום');
        if (formData.targetAudience.length === 0) errors.push('קהל יעד');
        break;
      case 4:
        if (!formData.paymentPlan.trim()) errors.push('תכנית תשלום');
        if (!agreed) errors.push('אישור תקנון');
        break;
      case 5:
        // Payment step - no validation needed
        break;
    }
    return errors;
  };

  // Validation logic for each step
  const isStepValid = () => {
    return getValidationErrors().length === 0;
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <BusinessInfoForm formData={formData} setFormData={setFormData} isShaliach={false} />;
      case 1:
        return (
          <CategorySelector
            selectedCategory={formData.category}
            setSelectedCategory={(cat: string) => setFormData(prev => ({ ...prev, category: cat, subCategory: [] }))}
            selectedSubcategory={formData.subCategory}
            setSelectedSubcategory={(subCat: string[]) => setFormData(prev => ({ ...prev, subCategory: subCat }))}
          />
        );
      case 2:
        return <DescriptionAndMediaForm formData={formData} setFormData={setFormData} />;
      case 3:
        return <AdditionalInfoForm formData={formData} setFormData={setFormData} />;
      case 4:
        return (
          <PaymentPlanSelector
            selectedPlan={formData.paymentPlan}
            setSelectedPlan={(plan: string) => setFormData(prev => ({ ...prev, paymentPlan: plan }))}
            agreed={agreed}
            setAgreed={setAgreed}
          />
        );
      case 5: {
        const getPriceWithVAT = () => {
          const prices = {
            monthly: { base: 500, withVAT: 590 },
            'half-yearly': { base: 800, withVAT: 944 },
            yearly: { base: 1200, withVAT: 1416 },
            'pilot-half-yearly': { base: 0, withVAT: 0 }
          };
          return prices[formData.paymentPlan as keyof typeof prices] || { base: 0, withVAT: 0 };
        };

        const price = getPriceWithVAT();

        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Box sx={{ mb: '0.35em' }}>
              <Typography variant="h3">שלב התשלום</Typography>
            </Box>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body">
                לאחר ביצוע התשלום המודעה תישלח לבדיקה ותופיע בלוח. לחץ להמשך כדי לפתוח את דף התשלום בטאב חדש.
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'inline-block',
                p: 3,
                border: '2px solid #2196f3',
                borderRadius: 2,
                backgroundColor: '#f5f5f5',
                mb: 3
              }}
            >
              <Box sx={{ mb: 1 }}>
                <Typography variant="h3">
                  תכנית נבחרת:{' '}
                  {formData.paymentPlan === 'monthly'
                    ? '3 חודשים'
                    : formData.paymentPlan === 'half-yearly'
                    ? '6 חודשים'
                    : formData.paymentPlan === 'yearly'
                    ? 'שנה'
                    : formData.paymentPlan === 'pilot-half-yearly'
                    ? 'פיילוט ל-6 חודשים'
                    : 'לא נבחרה'}
                </Typography>
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  מחיר: {price.base} ₪
                </Typography>
              </Box>
              {price.base > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    מע״מ (18%): {price.withVAT - price.base} ₪
                  </Typography>
                </Box>
              )}
              <Typography variant="h3" color="primary.main">
                סה״כ לתשלום: {price.withVAT} ₪
              </Typography>
            </Box>

            {price.base > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  ניתן לפרוס את התשלום עד 5 תשלומים ללא ריבית
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, gap: 2 }}>
              <Button variant="outlined" onClick={handleBack} fullWidth={false}>
                הקודם
              </Button>
              <Button onClick={handlePayment} loading={paymentLoading} fullWidth={false}>
                המשך לתשלום
              </Button>
            </Box>
          </Box>
        );
      }
      default:
        return 'Unknown step';
    }
  };

  return (
    <Container
      maxWidth="md"
      // Mobile: keep the step buttons clear of the floating bottom nav.
      sx={{ my: 4, pb: { xs: `calc(${MOBILE_BOTTOM_NAV_INSET} + 16px)`, md: 0 } }}
    >
      <Box sx={{ textAlign: 'center', mb: '0.35em' }}>
        <Typography variant="h1">פרסום מודעה בלוח העסקים</Typography>
      </Box>
      <Box sx={{ textAlign: 'center', mb: 2, whiteSpace: 'pre-wrap' }}>{PUBLISHER_EXPLANATION_TEXT}</Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 4 }}>
        <Link
          href={PUBLISHER_INFO_LINK}
          target="_blank"
          rel="noopener noreferrer"
          underline="always"
          sx={{ fontSize: '1rem' }}
        >
          פירוט והסבר על הפרסום בלוח
        </Link>
        <Link
          component="button"
          onClick={() => setRegulationsOpen(true)}
          underline="always"
          sx={{ fontSize: '1rem', cursor: 'pointer' }}
        >
          תקנון לוח חבד
        </Link>
      </Box>

      <WhatsAppSupportButton />

      {success ? (
        <SubmissionSuccess
          title="המודעה נשלחה בהצלחה!"
          message="פרטי העסק ששלחת ייבדקו על ידי מנהלי המערכת ויופיע בלוח בקרוב."
          onSubmitAnother={handleReset}
          submitAnotherText="פרסם מודעה נוספת"
        />
      ) : (
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <Box sx={{ mb: 4 }}>
            <Stepper activeStep={activeStep} orientation={isMobile ? 'vertical' : 'horizontal'} steps={steps} />
          </Box>

          {getStepContent(activeStep)}

          {activeStep !== 5 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button variant="outlined" disabled={activeStep === 0} onClick={handleBack} fullWidth={false}>
                הקודם
              </Button>
              <Button
                onClick={activeStep === 4 ? handleSubmit : handleNext}
                disabled={!isStepValid()}
                loading={loading || paymentLoading}
                fullWidth={false}
              >
                {activeStep === 4 ? 'צור מודעה' : 'הבא'}
              </Button>
            </Box>
          )}

          {error && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="error.main">
                {error}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Dialog
        open={regulationsOpen}
        onClose={() => setRegulationsOpen(false)}
        maxWidth="md"
        fullWidth
        title="תקנון לוח חבד"
        cancelAction={{ label: 'סגור' }}
      >
        <Box
          component="pre"
          sx={{
            whiteSpace: 'pre-wrap',
            fontFamily: 'inherit',
            margin: 0
          }}
        >
          {PUBLISHER_REGULATIONS}
        </Box>
      </Dialog>
    </Container>
  );
};

export default PublisherAdSubmissionPage;
